import React from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { useAdminAttention } from "../../hooks/useAdmin";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";

/**
 * Where each attention bucket sends you.
 *
 * The backend returns a stable `key` and no URL, so routing stays here. Each
 * entry names the permission required to open the destination: a bucket the
 * reader cannot act on is still counted, but is not offered as a dead link
 * that would only 403.
 */
const BUCKET_ROUTES = {
  transfers_stuck: {
    to: "/admin/transfers?status=processing",
    permission: PERMISSIONS.TRANSFERS_READ,
  },
  transfers_refunding: {
    to: "/admin/transfers?status=refunding",
    permission: PERMISSIONS.TRANSFERS_READ,
  },
  transfers_manual_review: {
    to: "/admin/transfers?status=manual_review",
    permission: PERMISSIONS.TRANSFERS_READ,
  },
  transfers_compliance_review: {
    to: "/admin/transfers?status=compliance_review",
    permission: PERMISSIONS.TRANSFERS_READ,
  },
  // Deliberately unrouted: no page lists settlements needing reconciliation.
  // Sending an operator to an approximate list is worse than not linking -
  // they would filter, find nothing matching, and assume the count is wrong.
  compliance_cases_open: {
    to: "/admin/compliance",
    permission: PERMISSIONS.COMPLIANCE_READ,
  },
  kyc_pending_too_long: {
    to: "/admin/users?kyc_status=pending",
    permission: PERMISSIONS.USERS_READ,
  },
};

const SEVERITY = {
  critical: { icon: AlertOctagon, badge: "bg-red-50 text-red-600 border-red-100" },
  warning: { icon: AlertTriangle, badge: "bg-amber-50 text-amber-700 border-amber-100" },
  info: { icon: Info, badge: "bg-sky-50 text-sky-700 border-sky-100" },
};

/**
 * What is waiting on a human right now.
 *
 * Read-derived, not pushed. There is no scheduler in the backend, so this is
 * the state of the queue when the page loaded (refreshed on a slow interval).
 * The footnote says so on purpose - a badge that looks like alerting but is not
 * is worse than no badge, because someone will rely on it to page them.
 */
export function AttentionPanel() {
  const { buckets, total, urgentTotal, stuckAfterMinutes, isLoadingAttention, attentionError } =
    useAdminAttention();
  const { can } = usePermissions();

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="text-sm font-semibold text-[#0A2540]">Needs attention</h3>
        {/* Urgent is called out separately from the total. A single figure
            would let a routine backlog (137 open KYC cases) bury the one item
            that is actually money at risk, and a headline number that is
            always large teaches people to stop reading the panel. */}
        {total > 0 && (
          <span className="text-xs font-semibold tabular-nums">
            {urgentTotal > 0 && (
              <span className="text-red-600">{urgentTotal} urgent</span>
            )}
            {urgentTotal > 0 && <span className="text-slate-300 mx-1.5">/</span>}
            <span className="text-[#0A2540]/50">{total} total</span>
          </span>
        )}
      </div>

      {/* A failed check must not look like an empty queue. Returning null on
          error made "nothing needs attention" and "we could not find out"
          identical on screen, which is the more dangerous of the two to get
          wrong - the same distinction the list pages already make. */}
      {attentionError ? (
        <div className="flex items-start gap-2 py-2">
          <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-[#0A2540]/70">
            Could not check what needs attention, so this list may be
            incomplete. Retrying automatically.
          </p>
        </div>
      ) : isLoadingAttention ? (
        <p className="text-sm text-slate-400 py-2">Checking…</p>
      ) : buckets.length === 0 ? (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 size={16} className="text-emerald-500" />
          <p className="text-sm text-[#0A2540]/70">
            Nothing is waiting. No stuck transfers, refunds, or open cases.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {buckets.map((bucket) => {
            const route = BUCKET_ROUTES[bucket.key];
            const openable = route && can(route.permission);
            const severity = SEVERITY[bucket.severity] ?? SEVERITY.info;
            const Icon = severity.icon;

            const row = (
              <div className="flex items-start gap-3 rounded-2xl border border-slate-100 p-3 transition group-hover:border-[#0A4730]/30 group-hover:bg-[#F5F9F4]/50">
                <span
                  className={`inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-xl border text-xs font-semibold tabular-nums ${severity.badge}`}
                >
                  {bucket.count}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0A2540] flex items-center gap-1.5">
                    <Icon size={13} className="shrink-0" />
                    {bucket.label}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{bucket.detail}</p>
                </div>
              </div>
            );

            return (
              <li key={bucket.key}>
                {openable ? (
                  <Link to={route.to} className="block group">
                    {row}
                  </Link>
                ) : (
                  row
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Say plainly what this is, so nobody mistakes it for alerting. */}
      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Counted when this page loads and refreshed each minute
        {stuckAfterMinutes ? `; "stuck" means no movement for ${stuckAfterMinutes} minutes` : ""}
        . This is not an alert — nothing here will page you.
      </p>
    </div>
  );
}
